import { CommonContainer } from 'src/models/Common'
import {
  customerContainer,
  cartContainer,
  listsContainer,
  accountsContainer,
  teamContainer,
  loyaltyContainer,
  accountsAddressContainer,
} from 'src/models'
import isMerchPLP, {
  isMerchandising,
  isStudioPreview,
  setLocalStorage,
  deleteFromLocalStorage,
  getLocalStorage,
  isB2BAccount,
  isB2BUser,
  isB2B2C,
  setCookie,
  shouldMakeB2BCalls,
  getAppConfig,
  setSessionStorage,
  getSessionStorage,
} from 'config/appConfig'
import { toastState } from 'src/views/components'
import {
  getLocaleCodeFromUrl,
  getLocalePathParam,
  application,
  enablePhoneVerification,
  convertToBoolean,
  trackImpactIdentify,
  trackSalesAIPixelLogin,
  enableNewFeature,
  IS_BROWSER,
} from 'src/utils'
import { checkConsentCookieAndEnableTracking } from 'src/utils/consent'
import { pageNames } from 'src/routes/pathParams'
import { isEnableReEnrollNotificationPopup } from 'src/views/components/Header/deps'
import { trackInsiderUserObject } from 'src/utils/insider'

function getQueryParam(location, key) {
  const fromLocation = location?.queryParams?.[key]
  if (fromLocation) {
    return fromLocation
  }
  if (typeof location?.search === 'string' && location.search.length > 0) {
    return new URLSearchParams(location.search).get(key) || ''
  }
  return ''
}

function isSignInPath(pathname = '') {
  return pathname === pageNames.signIn || /(^|\/)signin$/.test(pathname)
}

function isAuthCallbackPath(pathname = '') {
  return (
    pathname.includes(pageNames.loginCallback) ||
    pathname.includes(pageNames.logoutCallback)
  )
}

/**
 * SessionContainer class that extends CommonContainer.
 * Manages session state and authentication for the application.
 */
class SessionContainer extends CommonContainer {
  csrAdminName = ''
  notifyDirectSellerTimeout = null

  /**
   * Initializes the session by:
   * - Checking the user profile and registering guest if needed
   * - Fetching qualification data
   * - Getting subscription count if registered user
   * - Viewing the cart
   */
  init = async () => {
    console.log('^^^^^^^ session init called ^^^^^^^')

    await this.checkProfileMakeRegisterGuest()

    /**
     * @todo
     * !!! required to be revalidated
     */
    customerContainer.fetchQualificationData()
    if (customerContainer.isRegisterUser) {
      await customerContainer.qualificationUser()
      await cartContainer.getSubscriptionCount()
      // customerContainer.fetchQualificationData()
    }
    if (
      IS_BROWSER &&
      !(
        window?.location?.pathname?.includes(pageNames.klarnaCheckout) ||
        window?.location?.hash?.toLowerCase?.()?.includes('venmosuccess') ||
        (getLocalStorage('gpsRedirection') &&
          window?.location?.pathname?.includes(pageNames.expressCheckout))
      )
    ) {
      await cartContainer.viewCart(null, true)
    }
  }

  /**
   * Logs in the user with the provided form data.
   *
   * @param {Object} formData - The login form data.
   * @returns {Promise} A promise that resolves to the API response.
   */
  login = async formData => {
    const response = await customerContainer.login(formData)
    if (this.isSuccessResponse(response)) {
      await this.init()
    }
    localStorage.setItem('IsAuthenticated', true)
    return response
  }

  /**
   * Registers a new user with the provided form data.
   *
   * @param {Object} formData - The registration form data.
   * @returns {Promise} A promise that resolves to the API response.
   */
  registerNextGen = async formData => {
    const response = await customerContainer.registerNextGen(formData)
    return response
  }

  setReEnrollFlags = (terminated, hideTempPopup, reEnroll) => {
    customerContainer.isAccountTerminated = terminated
    customerContainer.isToHideTempPopupForReEnroll = hideTempPopup
    customerContainer.reEnroll = reEnroll
  }

  /**
   * Logs in the user with Okta authentication.
   *
   * @param {Object} formData - The login form data.
   * @param {Function} successCbk - Optional callback on successful login.
   * @returns {Promise} A promise that resolves to the API response.
   */
  oktaLogin = async (formData, successCbk) => {
    const {
      response,
      homeCountry,
      activeCountryCode = '',
    } = await customerContainer.oktaLogin(formData)
    if (response && this.isSuccessResponse(response)) {
      application.isCallInProgress = true
      setLocalStorage('IsAuthenticated', true)
      setLocalStorage('isOktaUser', true)
      listsContainer.initFavoritesAndSaveForLater(true)
      await this.init()
      const customerResponse = customerContainer.profileResponse?.id
        ? customerContainer.profileResponse
        : response?.customer
      trackInsiderUserObject({
        userData: customerResponse,
        eventType: 'user',
      })
      application.isCallInProgress = false
      customerContainer.isLoading = false
      const accountTerminated =
        response?.customer?.customProperties?.accountTerminated === 'true' ||
        false

      if (accountTerminated && isEnableReEnrollNotificationPopup()) {
        setSessionStorage('isAccountTerminated', true)
        this.setReEnrollFlags(true, true, true)
      } else {
        setSessionStorage('isAccountTerminated', false)
        this.setReEnrollFlags(false, false, false)
      }
      if (successCbk) {
        successCbk(response, homeCountry, activeCountryCode?.toLowerCase())
      }
      if (
        IS_BROWSER &&
        localStorage.getItem('isToShowNotifyDirectSellerPopup') == 'true'
      ) {
        const productDetails =
          localStorage?.getItem('skuDetailsForNotifyDirectSeller') &&
          JSON.parse(localStorage?.getItem('skuDetailsForNotifyDirectSeller'))
        if (productDetails) {
          cartContainer.skuDetailsForNotifyDirectSeller = {
            productId: productDetails?.productId,
            skuId: productDetails?.skuId,
          }
          cartContainer.isNotifyDirectSellerPopupOpen = true
        }
        localStorage.removeItem('isToShowNotifyDirectSellerPopup')
        localStorage.removeItem('skuDetailsForNotifyDirectSeller')
        localStorage.removeItem('redirectionURLForDirectSellerPopUp')
      }
    } else {
      if (enableNewFeature('enablePixelTracking')) {
        trackSalesAIPixelLogin({
          accountId: customerContainer?.accountId || '',
          personId: customerContainer?.personId || '',
          method: 'password',
          status: 'failed',
        })
      }

      toastState.setToastMessage(response?.message)

      /**
       * @important
       * we are already redirecting to previous page
       * via LoginCallback component, so we need to skip here
       */
      await customerContainer?.logout(true)
    }
  }

  /**
   * Authorizes the user with Okta authentication.
   *
   * Calls the customerContainer.oktaAuthorize() method to get the Okta authorize URL.
   * Parses the URL to get the redirect URI and nonce parameter.
   * Saves the redirect URI and nonce to local storage.
   *
   * If the authorization is successful, returns the response.
   * If not, sets an error toast message and redirects to the home page after 3 seconds.
   */ oktaAuthorize = async () => {
    const response = await customerContainer.oktaAuthorize()
    const oktaAuthorizeUrl = response?.oktaAuthorizeUrl || ''
    let oktaUrl = ''
    try {
      if (!oktaAuthorizeUrl) {
        throw new Error('oktaAuthorizeUrl is empty')
      }
      oktaUrl = new URL(oktaAuthorizeUrl)
    } catch (error) {
      console.error('Invalid OKTA Url', oktaAuthorizeUrl)
    }
    const redirectUri = oktaUrl?.searchParams?.get('state') || ''
    const nonceParam = oktaUrl?.searchParams?.get('nonce') || ''
    setLocalStorage('oktaUrlState', redirectUri)
    setLocalStorage('nonceParam', nonceParam)

    if (this.isSuccessResponse(response)) {
      return response
    } else {
      toastState.setToastMessage(response.message)

      if (typeof window !== 'undefined') {
        let locale = getLocaleCodeFromUrl() || ''
        if (locale == '') {
          locale =
            getLocalStorage?.('login_callback_locale')?.replace('-', '_') || ''
          // If login fails, and when login callback page url doesn't have locale with it
        }
        locale = locale?.toLowerCase()?.split('_')
        setTimeout(() => {
          window.location.href = `${window.location.origin}/${locale?.[1]}/${locale?.[0]}/`
        }, 3000)
      }
    }
  }

  /**
   * Registers a new user with the provided form data.
   *
   * Calls the customerContainer to register the user and get a response.
   * If registration is successful:
   * - Views the cart
   * - Initializes favorites and saved for later lists
   * - Gets loyalty points
   *
   * @param {Object} formData - Object containing registration form data
   * @returns {Object} API response
   */
  register = async formData => {
    const response = await customerContainer.register(formData)
    if (this.isSuccessResponse(response)) {
      cartContainer.viewCart()
      listsContainer.initFavoritesAndSaveForLater(true)
      await loyaltyContainer.getLoyaltyPoints(true)
    }
    return response
  }

  /**
   * Registers a guest user.
   *
   * If not a B2B account, calls the customerContainer to register a guest user
   * and initialize favorites and saved for later lists.
   *
   * If a B2B account, gets the account settings from the accounts container.
   *
   * @returns {Object} API response
   */
  guestRegister = async () => {
    if (!isB2BAccount()) {
      const response = await customerContainer.guestRegister()
      listsContainer.initFavoritesAndSaveForLater(false)
      return response
    }
    if (isB2BAccount() === true) {
      await accountsContainer.getAccountSettings()
      //await accountsContainer.getAllAttributes()
      //await accountsContainer.getAllAttributesWithoutSize()
    }
    return ''
  }

  /**
   * Gets the list services, optionally skipping getting the list.
   *
   * @param {boolean} skipGetList - Whether to skip getting the list
   */
  listServices = skipGetList => {
    console.log('list service here')
  }

  /**
   * Checks profile and registers guest if needed
   *
   * Gets customer profile. If unsuccessful and session not expired, registers guest.
   * If B2B account, gets account settings and other account info.
   * If should make B2B calls, gets additional account info.
   * If B2B user, gets account addresses.
   * Gets loyalty points for registered user if missing or request failed.
   *
   * @returns {Object} API response
   */
  checkProfileMakeRegisterGuest = async () => {
    const response = await customerContainer.getProfile({ isFromInit: true })
    if (!this.isSuccessResponse(response)) {
      if (!customerContainer.isSessionExpired) {
        this.guestRegister()
      }
    } else {
      this.trackImpact(customerContainer)
      checkConsentCookieAndEnableTracking(customerContainer)
      if (isB2BAccount()) {
        // accountsContainer.getContracts()
        //await accountsContainer.getAllAttributes()
        //await accountsContainer.getAllAttributesWithoutSize()
      }

      const isPhoneNumberVerified =
        response?.customProperties?.isPhoneNumberVerified || ''
      if (
        customerContainer.isRegisterUser &&
        isPhoneNumberVerified !== 'true' &&
        enablePhoneVerification()
      ) {
        customerContainer.fromSessionVerifyPhonePopup = true
      }

      if (
        customerContainer.isRegisterUser &&
        getSessionStorage('isAccountTerminated')
      ) {
        customerContainer.isLoading = false
        this.setReEnrollFlags(true, true, true)
      } else if (
        !customerContainer.isOktaUser &&
        customerContainer.isRegisterUser
      ) {
        customerContainer.isLoading = false
        this.setReEnrollFlags(false, false, false)
      }

      if (shouldMakeB2BCalls()) {
        await accountsContainer.getAccountSettings()

        accountsAddressContainer.getAllAccountAddress()
        accountsContainer.getAllBuyerRoles()
        await teamContainer.getAllTeams()
        // costCenterContainer.getAllCostCenter()
      }
      if (isB2BUser()) {
        accountsAddressContainer.getAllAccountAddress()
      }
      /**
       * @note
       * NUSKIN-439 Commenting redundant calls for refactoring
       */

      // if (customerContainer.isRegisterUser) {
      //   await addressContainer.getProfileAddress()
      //   await customerContainer.getPayment()
      // }

      // }

      /**
       * @important @note
       * loyalty API is must required for signed in users.
       * So, DON'T comment out the following condition
       */
      if (
        (Object.keys(loyaltyContainer?.loyaltyPointsResponse || {}).length ===
          0 ||
          !this.isSuccessResponse(loyaltyContainer.loyaltyPointsResponse)) &&
        customerContainer.isRegisterUser
      ) {
        await loyaltyContainer.getLoyaltyPoints(true)
      }
      // listsContainer.initFavoritesAndSaveForLater(true)
    }
    return response
  }
  /**
   * Reigsters customer id to impact
   * @param {Object} customerContainer - An object containing customer-related data.
   * @returns {void}
   */
  trackImpact = customerContainer => {
    // Cookie consent is not needed for Impact tracking
    const enableImpactTracking = convertToBoolean(
      getAppConfig().enableImpactTracking
    )
    if (enableImpactTracking) {
      trackImpactIdentify(customerContainer)
    }
  }
  /**
   * Logs in as a CSR admin user using provided credentials
   * @param {Object} props - Object containing token and userId
   * @returns {Promise} Makes API call to log in and sets cookies/localStorage
   */
  loginAsCSR = async props => {
    const { token, userId, storeId } = props
    const { csrAdminCookie } = getAppConfig()
    const loadParams = {
      endPointName: 'loginAsCSR',
      queryParams: {
        token,
        userId,
      },
    }

    if (storeId) {
      setLocalStorage('dynamicStoreId', storeId)
    }

    try {
      const response = await this.fetchResponse(loadParams)
      if (this.isSuccessResponse(response)) {
        setLocalStorage('x-store-id', props.storeId)
        const firstName = response?.firstName || ''
        const lastName = response?.lastName || ''
        const name = firstName || lastName ? `${firstName} ${lastName}` : ''
        setCookie({
          cookieName: csrAdminCookie,
          cookieValue: name,
          ignoreCommonHeader: true,
        })
        if (token) {
          setLocalStorage('check', true)
          setLocalStorage('phoneVerifyFromSession', true)
        }
        if (name) {
          setLocalStorage('csrAdminName', name)
        }
        this.csrAdminName = name
      } else {
        toastState.setToastMessage(response.message)
      }
      return response
    } finally {
      deleteFromLocalStorage('dynamicStoreId')
    }
  }

  /**
   * Refreshes the user's session by calling the refreshSession API endpoint.
   * Updates the session data if the API call is successful.
   * Shows a toast message if the API call fails.
   */
  refreshSession = async () => {
    const loadParams = {
      endPointName: 'refreshSession',
    }

    const response = await this.fetchResponse(loadParams)
    if (this.isSuccessResponse(response)) {
      console.log('refreshSession Success-response ==>', response)
    } else {
      toastState.setToastMessage(response.message)
    }
    return response
  }

  shouldRunBootEffects = () => {
    return (
      (isStudioPreview && isB2BAccount()) ||
      (!isStudioPreview && !isMerchPLP && !isMerchandising)
    )
  }

  checkAndRedirectPage = (history, location) => {
    const { pathname, search } = location || {}

    if (isAuthCallbackPath(pathname)) {
      return
    }

    const { myaccount } = pageNames

    const isRegisterUser = customerContainer.isRegisterUser
    const isSignInPage = isSignInPath(pathname)
    const authorizedPagePrefix = myaccount.split('/')[1]

    const commonPage =
      pathname === pageNames.configs ||
      pathname === pageNames.status ||
      pathname === pageNames.resetPassword ||
      pathname === pageNames.userActivation

    const isUserAuthorizedPage = pathname.includes(`/${authorizedPagePrefix}/`)
    const isGuestB2B = customerContainer.isGuestB2B && !isB2B2C()

    if (isRegisterUser && isSignInPage) {
      const localePath = getLocalePathParam()
      history.push(localePath ? `/${localePath}` : pageNames.home)
      return
    }
    if (!isRegisterUser && isUserAuthorizedPage) {
      history.push(`${pageNames.signIn}?targetURL=${pathname}${search}`)
      return
    }
    if (isGuestB2B && !commonPage && !isSignInPage) {
      history.push(`${pageNames.signIn}`)
    }
  }

  bootFromRoute = async (history, location, options = {}) => {
    const isCancelled = () => options.isCancelled?.() === true
    const token = getQueryParam(location, 'token')

    if (token) {
      const userId = getQueryParam(location, 'uid')
      const storeId = getQueryParam(location, 'storeid')
      await this.loginAsCSR({ token, userId, storeId })
      if (isCancelled()) {
        return
      }
      // Legacy CommonHeader order: navigate home first, then init() (fall-through).
      history.push(pageNames.home)
      if (isCancelled()) {
        return
      }
      await this.init()
      return
    }

    await this.init()
    if (isCancelled()) {
      return
    }

    const csrAdminName = getLocalStorage('csrAdminName') || ''
    if (!csrAdminName && customerContainer.isSessionExpired === true) {
      this.checkAndRedirectPage(history, history.location || location)
    }
  }
}

const sessionContainer = new SessionContainer()

export { sessionContainer }
export default sessionContainer
